/**
 * Noibu Feature Flag: home page landing for paid social, in place on /
 * (flag "2083-home-page-landing-for-paid-social", Noibu A/B test 226).
 *
 * Loaded by sections/noibu-paid-home-ab.liquid (header group), which also
 * emits the CSS that each class below switches:
 *
 *   original                  ->  html.nff-paid--a  (control: today's home page)
 *   paid-social-landing-page  ->  html.nff-paid--b  (the landing sections)
 *
 * Same class-toggle shape as assets/noibu-flag-ferrari-collection.js and
 * assets/noibu-flag-sticky-checkout.js. Separate file on purpose: those
 * define the arms of other experiments and must not change mid-flight. The
 * running "home-page" test (#32, assets/noibu-feature-flag.js, classes
 * nff-home--a/b) keeps its own file and classes; this script never touches
 * them, and in B the gate hides both of #32's arms.
 *
 * Fail-safe is the control. No class is set unless the SDK returns a real
 * assignment (a variant present that is not the '__excluded' sentinel, no
 * errorCode, reason TARGETING_MATCH / SPLIT / CACHED); untargeted visitors,
 * a stopped flag (FLAG_NOT_FOUND), SDK errors and the 10 s timeout all leave
 * the page exactly as it is today. A page the hint already painted from a
 * recent real decision is never swapped mid-visit.
 *
 * Noibu's implementation code for test 226 (console > A/B tests >
 * Implementation), in the shape the console generates:
 *
 *   function renderPaidSocialLandingPage(container) {
 *     // TODO: the implementation for variation B
 *   }
 *
 *   function renderOriginal(container) {
 *     // TODO: the implementation for the default experience
 *   }
 *
 *   function render(container) {
 *     const { variation } = client.getStringDetails(
 *       "2083-home-page-landing-for-paid-social",
 *       "original"
 *     );
 *
 *     switch (variation) {
 *       case "paid-social-landing-page":
 *         // Variation B
 *         return renderPaidSocialLandingPage(container);
 *       case "original":
 *         // Variation A (control)
 *         // Since this is the original experience, add no code here and
 *         // let this fall through to the default.
 *       default:
 *         // This should always be the original experience. Users excluded
 *         // by targeting will see this.
 *         return renderOriginal(container);
 *     }
 *   }
 *
 *   // `client` is the ready client from the Noibu A/B testing SDK.
 *   // Call render(container) once your feature flag client is ready.
 *
 * SDK NOTE: the snippet destructures `variation`, but the details object the
 * SDK returns carries the arm as `variant` (and `value`), e.g.
 * { variant: 'show-reassurance', value: 'show-reassurance', reason: 'CACHED' }
 * for 2083-cart-reassurance on this store (checked Oct 8, 2026). Copied
 * verbatim, every visitor would fall through to the control. readAssignment()
 * reads `variant`, then `variation`, then `value`.
 */
(function () {
  'use strict';

  var FLAG = '2083-home-page-landing-for-paid-social';
  var CONTROL = 'original';
  var VARIATION_B = 'paid-social-landing-page';

  // Written as {"v": <arm>, "t": <ms>}; the gate's paint-time hint reads it
  // and ignores entries older than 7 days.
  var CACHE_KEY = 'nff.paid-home';

  var REPORT_KEY = 'nff_paid-home';
  var STATE_ATTR = 'data-nff-paid-state';
  var ARM_ATTR = 'data-nff-paid';
  var FORCE_PARAM = 'nff_paid';
  var CLASS_A = 'nff-paid--a';
  var CLASS_B = 'nff-paid--b';

  // Anchored to window.load because the client cannot arrive before it:
  // collect-core is injected by a ScriptTag on load, and collect-ff by
  // collect-core after that.
  var SDK_TIMEOUT_MS = 10000;

  var ASSIGNED_REASONS = { TARGETING_MATCH: true, SPLIT: true, CACHED: true, STATIC: true };

  var root = document.documentElement;

  /* ------------------------------------------------------------------ */
  /* Arms                                                                */
  /* ------------------------------------------------------------------ */

  function renderOriginal() {
    root.classList.remove(CLASS_B);
    root.classList.add(CLASS_A);
    root.setAttribute(ARM_ATTR, CONTROL);
  }

  function renderPaidSocialLandingPage() {
    root.classList.remove(CLASS_A);
    root.classList.add(CLASS_B);
    root.setAttribute(ARM_ATTR, VARIATION_B);
  }

  /* ------------------------------------------------------------------ */
  /* Flag plumbing (same shape as the other noibu-flag-*.js files)       */
  /* ------------------------------------------------------------------ */

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);

    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, showing the control: ' + detail);
    }

    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // collect-core may still be booting. Retry rather than drop the exposure.
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return;
      timer = setTimeout(function () {
        finish(
          onUnavailable,
          'no client ' + SDK_TIMEOUT_MS + 'ms after window.load. Is ' +
            'NOIBUJS_CONFIG.feature_flag_key provisioned for this store?'
        );
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  function remember(variation) {
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify({ v: variation, t: Date.now() }));
    } catch (error) {
      /* no-op: private mode or an embedded webview */
    }
  }

  function forget() {
    try {
      window.localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      /* no-op */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_paid=paid-social-landing-page or =original
    try {
      var value = new URLSearchParams(window.location.search).get(FORCE_PARAM);
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable: fall through to the flag. */
    }
    return null;
  }

  function readAssignment(client) {
    // See SDK NOTE in the header. Returns { arm, inTest, excluded, reason, errorCode }.
    if (typeof client.getStringDetails !== 'function') {
      return { arm: client.getStringValue(FLAG, CONTROL), inTest: true, excluded: false, reason: 'VALUE_ONLY', errorCode: '' };
    }

    var details = client.getStringDetails(FLAG, CONTROL) || {};
    var arm = details.variant || details.variation || details.value || CONTROL;
    var hasVariant = !!(details.variant || details.variation);
    var errored = !!details.errorCode;
    var reason = details.reason || '';

    // A visitor outside the targeting (no Meta utm_source / utm_medium paid
    // on this visit) comes back as { variant: '__excluded', value:
    // 'original', reason: 'CACHED', errorCode: '' } on this store (checked
    // Oct 8, 2026): a sentinel, not an arm. Noibu does not count that visit.
    var excluded = typeof arm === 'string' && arm.indexOf('__') === 0;
    var inTest = hasVariant && !errored && !excluded && !!ASSIGNED_REASONS[reason];

    return { arm: arm, inTest: inTest, excluded: excluded, reason: reason, errorCode: details.errorCode || '' };
  }

  function render(client) {
    var assignment = readAssignment(client);

    if (assignment.excluded) {
      // Outside the targeting on this visit. Leave the page as the hint
      // painted it (a returning B visitor keeps B, everyone else keeps
      // today's page) and leave the cache alone, so the experience stays
      // consistent across visits even though this one is not counted.
      if (!root.hasAttribute(ARM_ATTR)) root.setAttribute(ARM_ATTR, 'none');
      return 'not-in-test:excluded';
    }

    if (!assignment.inTest) {
      // Stopped flag (FLAG_NOT_FOUND), DEFAULT, or an evaluation the SDK did
      // not stand behind: out of the numbers. Forget the cache so the next
      // load is today's page, but never swap a page the visitor is reading.
      forget();
      if (!root.hasAttribute(ARM_ATTR)) root.setAttribute(ARM_ATTR, 'none');
      return 'not-in-test:' + (assignment.errorCode || assignment.reason || 'no-variant');
    }

    switch (assignment.arm) {
      case VARIATION_B:
        renderPaidSocialLandingPage();
        remember(VARIATION_B);
        return VARIATION_B;
      case CONTROL:
      // falls through
      default:
        renderOriginal();
        remember(CONTROL);
        return CONTROL;
    }
  }

  var forced = forcedVariation();
  if (forced) {
    // The gate's inline hint already set the class. Skip remember() and the
    // SDK so a forced preview neither poisons the cache nor records an
    // exposure.
    root.setAttribute(STATE_ATTR, 'forced');
    if (forced === CONTROL) renderOriginal();
    else renderPaidSocialLandingPage();
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));
      // No PROVIDER_CONFIGURATION_CHANGED handler on purpose: a visitor who
      // is reading the page should never have it swapped mid-visit. The next
      // page load re-evaluates.
    },
    function (detail) {
      // Do NOT remember() here: caching a value the SDK never produced would
      // replay a decision that was never made. A class set by the hint from
      // a recent real decision is left alone.
      if (!root.hasAttribute(ARM_ATTR)) root.setAttribute(ARM_ATTR, 'none');
      reportState('unavailable', detail);
    }
  );
})();
